import * as React from "react";

import {
  Autocomplete,
  TextInput,
  type TextInputSize,
} from "@/shared/foundation/primitives";

import { isValueless, type StringComparator } from "../FilterSchema";
import type { SuggestionsProvider } from "../suggestions";
import { useSuggestionPage } from "../useSuggestionPage";

import styles from "./valueInputs.module.css";

interface StringValueInputProps {
  comparator: StringComparator;
  value: string[];
  onChange: (next: string[]) => void;
  /**
   * Take focus on mount. The filter menu wants it; an editor that renders
   * several inputs at once does not. @default true
   */
  autoFocus?: boolean;
  /** Marks the input invalid, for an error shown beside it. */
  invalid?: boolean;
  /** Field size, on the `TextInput` scale. @default "md" */
  size?: TextInputSize;
  /**
   * Values to suggest as the author types. Hints only: the typed text is the
   * value, and picking a suggestion fills it in without changing the
   * comparator. A new source should remount the input (key it by field).
   */
  suggestions?: SuggestionsProvider;
}

export function StringValueInput({
  comparator,
  value,
  onChange,
  autoFocus = true,
  invalid = false,
  size = "md",
  suggestions,
}: StringValueInputProps) {
  const valueless = isValueless(comparator);

  if (valueless) {
    return null;
  }

  if (suggestions !== undefined) {
    return (
      <SuggestedStringInput
        value={value[0] ?? ""}
        onChange={(next) => onChange([next])}
        suggestions={suggestions}
        autoFocus={autoFocus}
        invalid={invalid}
        size={size}
      />
    );
  }

  return (
    <TextInput
      autoFocus={autoFocus}
      aria-invalid={invalid || undefined}
      size={size}
      placeholder="Enter value..."
      value={value[0] ?? ""}
      onChange={(event) => {
        onChange([event.target.value]);
      }}
    />
  );
}

/**
 * Most rows the suggestions show, the typed-text row and the summary line
 * included: a short list to pick from at a glance, refined by typing rather
 * than scrolling.
 */
const MAX_ROWS = 5;

/** A suggestion, or the row that keeps the typed text as it is. */
type SuggestionRow =
  | { kind: "suggestion"; value: string; count: number | undefined }
  | { kind: "typed"; value: string };

interface SuggestedStringInputProps {
  value: string;
  onChange: (next: string) => void;
  suggestions: SuggestionsProvider;
  autoFocus: boolean;
  invalid: boolean;
  size: TextInputSize;
}

/**
 * The value as free text, with the source's suggestions in a popup. The popup
 * opens when the author clicks into the field, types, or presses ArrowDown,
 * never on the focus the field takes when it mounts: an open popup would hide
 * the rest of the form from assistive technology the moment a field is picked.
 * It stays shut while there is neither a row nor a loading or error status to
 * show.
 *
 * At most `MAX_ROWS` rows show values, with counts where the source has them.
 * When no row shown spells the typed text exactly, the last row keeps the
 * typed text as it is; before anything is typed, the source's summary takes
 * the last row's place. Nothing is highlighted until the author moves onto a row,
 * so Enter applies what was typed.
 */
function SuggestedStringInput({
  value,
  onChange,
  suggestions,
  autoFocus,
  invalid,
  size,
}: SuggestedStringInputProps) {
  const { page, isLoading, isError } = useSuggestionPage(suggestions, value);
  const [open, setOpen] = React.useState(false);

  const typed = value.trim();
  const summary = typed === "" ? page?.summary : undefined;
  const rowBudget = summary === undefined ? MAX_ROWS : MAX_ROWS - 1;
  const rows = React.useMemo((): SuggestionRow[] => {
    const values = page?.values ?? [];
    const counts = page?.counts;
    const toRow = (suggestion: string): SuggestionRow => ({
      kind: "suggestion",
      value: suggestion,
      // Own keys only: a value named like an inherited property ("toString")
      // that the source doesn't count must show no count.
      count:
        counts !== undefined && Object.hasOwn(counts, suggestion)
          ? counts[suggestion]
          : undefined,
    });
    // With no suggestions the typed-text row would be the only one, which
    // says nothing the field doesn't already show.
    if (values.length === 0) {
      return [];
    }
    // Judged against the rows shown: a suggestion spelling the typed text
    // exactly, but past the last row, would otherwise leave no row for it.
    const shown = values.slice(0, rowBudget);
    if (typed === "" || shown.includes(typed)) {
      return shown.map(toRow);
    }
    return [
      ...shown.slice(0, rowBudget - 1).map(toRow),
      { kind: "typed", value: typed },
    ];
  }, [page, typed, rowBudget]);

  let status: string | null = null;
  if (isError) {
    status = "Suggestions unavailable";
  } else if (isLoading && rows.length === 0) {
    status = "Searching…";
  }

  return (
    <Autocomplete
      items={rows}
      value={value}
      onValueChange={onChange}
      // Held shut while there is nothing to show, so a field with no stored
      // values doesn't open an empty box.
      open={open && (rows.length > 0 || status !== null)}
      onOpenChange={setOpen}
      // The source has already matched the rows, and the typed row must stay.
      filter={null}
      itemToStringValue={(row) => row.value}
      size={size}
    >
      <Autocomplete.Input
        autoFocus={autoFocus}
        aria-invalid={invalid || undefined}
        placeholder="Enter value..."
        // Base UI closes the popup when focus leaves, but only while it sees
        // it open. Held shut for want of rows, it never asks, so close it
        // here: otherwise a later refetch's status line would open it with
        // focus elsewhere.
        onBlur={() => setOpen(false)}
      />
      <Autocomplete.Content
        busy={isLoading}
        // Always below the field, shrinking if it must: opening upward would
        // cover the operators chosen just above it.
        collisionAvoidance={{ side: "none" }}
      >
        <Autocomplete.Status>{status}</Autocomplete.Status>
        <Autocomplete.List>
          {(row: SuggestionRow) =>
            row.kind === "typed" ? (
              <Autocomplete.Item key="typed" value={row}>
                <Autocomplete.UseTyped text={row.value} />
              </Autocomplete.Item>
            ) : (
              // Prefixed so a suggestion spelled "typed" can't share the typed
              // row's key.
              <Autocomplete.Item key={`suggestion:${row.value}`} value={row}>
                <span className={styles.suggestionRow}>
                  <Autocomplete.MatchText text={row.value} />
                  {row.count !== undefined && (
                    <span className={styles.suggestionCount}>
                      {row.count.toLocaleString()}
                    </span>
                  )}
                </span>
              </Autocomplete.Item>
            )
          }
        </Autocomplete.List>
        <Autocomplete.Footer>{summary}</Autocomplete.Footer>
      </Autocomplete.Content>
    </Autocomplete>
  );
}
